import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface Case {
  id: string;
  title: string;
  status: string;
  client: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  priority?: 'low' | 'medium' | 'high';
  dueDate?: string;
  tags?: string[];
}

interface CasesContextType {
  cases: Case[];
  loading: boolean;
  error: string | null;
  fetchCases: () => Promise<void>;
  addCase: (caseData: Omit<Case, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCase: (id: string, caseData: Partial<Case>) => Promise<void>;
  deleteCase: (id: string) => Promise<void>;
  getCaseById: (id: string) => Case | undefined;
}

const CasesContext = createContext<CasesContextType | undefined>(undefined);

export const useCases = () => {
  const context = useContext(CasesContext);
  if (context === undefined) {
    throw new Error('useCases must be used within a CasesProvider');
  }
  return context;
};

export const CasesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const didInitRef = React.useRef(false);
  const shouldPrefetch = String(location?.pathname || '').startsWith('/cases');

  // ✅ FIX: Wrap fetchCases in useCallback to prevent infinite loops
  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const svc = (await import('../services/CaseApiService')).default as any;
      const resp = await svc.getCases({ limit: 50, sortBy: 'createdAt', sortDir: 'desc' });
      const items = (resp as any)?.items || (resp as any)?.data?.items || [];
      setCases(items as any);
      setError(null);
    } catch (err: any) {
      // Swallow 429s to avoid loops; keep last known cases
      if (
        String(err?.message || '').includes('RATE_LIMIT_EXCEEDED') ||
        err?.response?.status === 429
      ) {
        console.warn('CasesContext: rate limited; keeping current cases');
        setError(null);
      } else {
        setError('Failed to fetch cases');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ FIX: Wrap all functions in useCallback
  const addCase = useCallback(async (caseData: Omit<Case, 'id' | 'createdAt' | 'updatedAt'>) => {
    setLoading(true);
    try {
      const api = (await import('../services/api')).default;
      const newCaseRes = await api.post('/cases', caseData);
      const payload = newCaseRes?.data ?? newCaseRes;
      const newCase = payload && payload.data ? payload.data : payload;
      setCases(prev => [...prev, newCase as any]);
      setError(null);
    } catch (err) {
      setError('Failed to add case');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCase = useCallback(async (id: string, caseData: Partial<Case>) => {
    setLoading(true);
    try {
      const api = (await import('../services/api')).default;
      const updatedRes = await api.patch(`/cases/${id}`, caseData);
      const payload = updatedRes?.data ?? updatedRes;
      const updatedCase = payload && payload.data ? payload.data : payload;
      setCases(prev =>
        prev.map(c => (String((c as any).id) === String(id) ? (updatedCase as any) : (c as any)))
      );
      setError(null);
    } catch (err) {
      setError('Failed to update case');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCase = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const apiClient = (await import('../services/apiClient')).default;
      await apiClient.delete(`/cases/${id}`);
      setCases(prev => prev.filter(c => c.id !== id));
      setError(null);
    } catch (err) {
      setError('Failed to delete case');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getCaseById = useCallback(
    (id: string) => {
      return cases.find(c => c.id === id);
    },
    [cases]
  );

  // ✅ FIX: fetchCases is now stable via useCallback
  useEffect(() => {
    if (!shouldPrefetch || didInitRef.current) return;
    didInitRef.current = true;
    fetchCases();
  }, [fetchCases, shouldPrefetch]);

  const value = {
    cases,
    loading,
    error,
    fetchCases,
    addCase,
    updateCase,
    deleteCase,
    getCaseById,
  };

  return <CasesContext.Provider value={value}>{children}</CasesContext.Provider>;
};
